import { useMutation, useQueryClient } from 'react-query';
import { useCurrentUser, useToastProvider } from '@qualio/ui-components';
import formsApi from '../api/forms.api';
import {
  DefaultErrorMessage,
  SentForReview,
  SentForApproval,
  CannotSendForReview,
  CannotSendForApproval,
  WasSentForReview,
  WasSentForApproval,
  MissingRequiredFieldsWithApproversPrefix,
  MissingRequiredFieldsPrefix,
  ClickHere,
  ToViewRequiredEventFieldsSuffix,
} from '../displayStrings';
import { useEventFormParams } from './useEventFormParams';

export const useFormStatusChange = (
  formTitle: string,
  status: 'for_review' | 'for_approval',
  isNoQualityApprover?: boolean,
) => {
  const { eventId, stepId, formId } = useEventFormParams();
  const { companyId } = useCurrentUser();
  const { showToast } = useToastProvider();
  const queryClient = useQueryClient();

  const toastTitle = status === 'for_review' ? SentForReview : SentForApproval;
  const toastErrorTitle = status === 'for_review' ? CannotSendForReview : CannotSendForApproval;
  const toastDescription = status === 'for_review' ? WasSentForReview(formTitle) : WasSentForApproval(formTitle);
  const successDataCy =
    status === 'for_review' ? 'qe-form-sent-for-review-success-toast' : 'qe-form-sent-for-approval-success-toast';
  const errorDataCy =
    status === 'for_review' ? 'qe-form-send-for-review-error-toast' : 'qe-form-send-for-approval-error-toast';

  return useMutation(() => formsApi.updateForm(companyId, eventId, stepId, formId, { status: status }), {
    onSuccess: async () => {
      await queryClient.invalidateQueries(['formDetails', formId]);
      await queryClient.invalidateQueries(['reviewersList', companyId, formId]);
      await queryClient.invalidateQueries(['approversList', companyId, formId]);
      showToast({
        title: toastTitle,
        description: toastDescription,
        status: 'success',
        duration: 5000,
        'data-cy': successDataCy,
      });
    },
    onError: (err: any) => {
      const message = err?.response?.data?.message || DefaultErrorMessage;

      const formatMessage = (message: string) => {
        // prettier-ignore
        const msg = isNoQualityApprover ? (
          <span>
            {`${MissingRequiredFieldsWithApproversPrefix} `}
            <a href={`/quality-events/events/${eventId}`}>{ClickHere}</a>
            {` ${ToViewRequiredEventFieldsSuffix}`}
          </span>
        ) : (
          <span>
            {`${MissingRequiredFieldsPrefix} `}
            <a href={`/quality-events/events/${eventId}`}>{ClickHere}</a>
            {` ${ToViewRequiredEventFieldsSuffix}`}
          </span>
        );
        if (message === 'Cannot send for approval as there are missing required fields in event') {
          return msg;
        }
        return message;
      };
      showToast({
        description: formatMessage(message),
        status: 'error',
        duration: 15000,
        id: eventId,
        replace: true,
        title: toastErrorTitle,
        'data-cy': errorDataCy,
      });
    },
  });
};
